import React from 'react';
import { Link } from './LocaleLink';

/**
 * SVG diagram: two mini wheel icons connected by an arrow.
 *
 * Purely decorative — the actual Linked Wheels page has the real interface.
 * `prefers-reduced-motion` is respected by the subtle pulse animation.
 */
const LinkedWheelsDiagram: React.FC = () => (
  <svg
    viewBox="0 0 220 100"
    className="w-full max-w-[240px] h-auto"
    aria-hidden="true"
    role="img"
  >
    {/* Wheel 1 */}
    <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-violet-400" />
    {/* Segments */}
    <line x1="50" y1="20" x2="50" y2="50" stroke="currentColor" strokeWidth="1.5" className="text-violet-300" />
    <line x1="50" y1="50" x2="76" y2="35" stroke="currentColor" strokeWidth="1.5" className="text-violet-300" />
    <line x1="50" y1="50" x2="76" y2="65" stroke="currentColor" strokeWidth="1.5" className="text-violet-300" />
    <line x1="50" y1="50" x2="50" y2="80" stroke="currentColor" strokeWidth="1.5" className="text-violet-300" />
    <line x1="50" y1="50" x2="24" y2="65" stroke="currentColor" strokeWidth="1.5" className="text-violet-300" />
    <line x1="50" y1="50" x2="24" y2="35" stroke="currentColor" strokeWidth="1.5" className="text-violet-300" />
    <circle cx="50" cy="50" r="4" className="fill-violet-500" />
    {/* Pointer 1 */}
    <polygon points="50,16 46,8 54,8" className="fill-amber-400" />

    {/* Arrow */}
    <line x1="88" y1="50" x2="128" y2="50" stroke="currentColor" strokeWidth="2" className="text-gray-500 dark:text-gray-400" strokeDasharray="4 3" />
    <polygon points="132,50 124,44 124,56" className="fill-gray-400 dark:fill-gray-500" />

    {/* Wheel 2 */}
    <circle cx="170" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-violet-400" />
    <line x1="170" y1="20" x2="170" y2="50" stroke="currentColor" strokeWidth="1.5" className="text-violet-300" />
    <line x1="170" y1="50" x2="196" y2="35" stroke="currentColor" strokeWidth="1.5" className="text-violet-300" />
    <line x1="170" y1="50" x2="196" y2="65" stroke="currentColor" strokeWidth="1.5" className="text-violet-300" />
    <line x1="170" y1="50" x2="170" y2="80" stroke="currentColor" strokeWidth="1.5" className="text-violet-300" />
    <line x1="170" y1="50" x2="144" y2="65" stroke="currentColor" strokeWidth="1.5" className="text-violet-300" />
    <line x1="170" y1="50" x2="144" y2="35" stroke="currentColor" strokeWidth="1.5" className="text-violet-300" />
    <circle cx="170" cy="50" r="4" className="fill-violet-500" />
    {/* Pointer 2 */}
    <polygon points="170,16 166,8 174,8" className="fill-amber-400" />
  </svg>
);

const LinkedWheelsSpotlight: React.FC = () => (
  <section className="py-14 bg-gray-50 dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row items-center gap-10 sm:gap-14">
        {/* Left: copy */}
        <div className="flex-1 text-center sm:text-start">
          <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white mb-3">
            Chain wheels together
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-5">
            Link two or more wheels so the result of one spin feeds the next — perfect for multi-stage
            random selections where context matters.
          </p>
          <ul className="space-y-2.5 text-sm text-gray-600 dark:text-gray-300 mb-6">
            <li className="flex items-start gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400 flex items-center justify-center text-xs font-bold mt-0.5">✓</span>
              Chain spinners together in sequence
            </li>
            <li className="flex items-start gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400 flex items-center justify-center text-xs font-bold mt-0.5">✓</span>
              Conditional entries based on prior result
            </li>
            <li className="flex items-start gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400 flex items-center justify-center text-xs font-bold mt-0.5">✓</span>
              Export run history as CSV
            </li>
          </ul>
          <Link
            to="/linked-wheels/"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-violet-600 dark:text-violet-400 hover:underline"
          >
            Try Linked Wheels
            <span aria-hidden="true" className="inline-block ms-0.5">→</span>
          </Link>
        </div>

        {/* Right: diagram */}
        <div className="flex-shrink-0">
          <LinkedWheelsDiagram />
        </div>
      </div>
    </div>
  </section>
);

export default LinkedWheelsSpotlight;

